import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { fmtDate, normalizeStatus, STATUS_LABELS } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, FilePdf, Receipt, Trash, PencilSimple, ArrowRight, DownloadSimple, Signature, Factory, CheckCircle, Package, PaperPlaneTilt, Check, X, Plus, Archive, ArrowClockwise, Lock, LockOpen, Prohibit } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SignaturePad from "@/components/SignaturePad";
import DateInput from "@/components/DateInput";

export default function WorkOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner } = useAuth();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [savingSig, setSavingSig] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidPassword, setVoidPassword] = useState("");
  const [voiding, setVoiding] = useState(false);

  const load = () => api.get(`/work-orders/${id}`).then((r) => { setData(r.data); setForm(r.data.work_order); });
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="text-slate-400">Loading…</div>;
  const { work_order: wo, customer, invoice } = data;

  const save = async () => {
    const payload = {
      ...form,
      quantity: Number(form.quantity) || 1,
      deadline: form.deadline || null,
      customer_id: wo.customer_id,
      material_source: form.material_source || "shop",
    };
    try {
      await api.put(`/work-orders/${id}`, payload);
      toast.success("Saved");
      setEdit(false); load();
    } catch (err) {
      if (err?.response?.status === 423) {
        toast.error(err.response.data?.detail || "Work Order is locked");
      } else {
        toast.error("Save failed");
      }
    }
  };

  const uploadArtwork = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => ({ ...f, artwork_file_path: up.path, artwork_file_name: up.filename }));
    toast.success("Uploaded — click Save to attach");
  };

  const convertToInvoice = async () => {
    if (invoice) { navigate(`/invoices/${invoice.id}`); return; }
    const { data: inv } = await api.post(`/work-orders/${id}/convert-to-invoice`);
    toast.success(`Invoice ${inv.number} created`);
    navigate(`/invoices/${inv.id}`);
  };

  const remove = async () => {
    if (!window.confirm("Archive this work order? It can be restored from the Archived tab.")) return;
    await api.post(`/work-orders/${id}/archive`);
    toast.success("Archived");
    navigate("/work-orders");
  };

  const doVoid = async () => {
    if (!voidPassword) { toast.error("Enter your password to confirm"); return; }
    setVoiding(true);
    try {
      await api.post(`/work-orders/${id}/void`, { password: voidPassword });
      toast.success("Work Order voided");
      setVoidOpen(false); setVoidPassword("");
      navigate("/work-orders");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Void failed");
    } finally {
      setVoiding(false);
    }
  };

  const restore = async () => {
    await api.post(`/work-orders/${id}/restore`);
    toast.success("Restored");
    load();
  };

  const toggleLock = async () => {
    try {
      await api.post(`/work-orders/${id}/lock`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not toggle lock");
    }
  };

  const saveSignature = async (dataUrl, signedBy) => {
    setSavingSig(true);
    try {
      await api.post(`/work-orders/${id}/sign`, { signature_data_url: dataUrl, signed_by: signedBy });
      toast.success("Signature saved");
      load();
    } catch { toast.error("Failed to save signature"); }
    finally { setSavingSig(false); }
  };
  const clearSignature = async () => {
    if (!window.confirm("Remove existing signature?")) return;
    await api.delete(`/work-orders/${id}/sign`);
    toast.success("Signature cleared"); load();
  };

  const setStatus = async (nextStatus) => {
    const current = normalizeStatus(wo.status);
    if (current === nextStatus) return;
    // Warn owner about the auto-email side effect
    if (nextStatus === "fulfilled" && customer?.email && wo.invoice_id) {
      const ok = window.confirm(
        `Mark this work order Fulfilled and email invoice PDF to ${customer.email} automatically?`
      );
      if (!ok) return;
    }
    try {
      const payload = {
        customer_id: wo.customer_id,
        job_type: wo.job_type,
        material: wo.material || "",
        dimensions: wo.dimensions || "",
        color: wo.color || "",
        thickness: wo.thickness || "",
        material_source: wo.material_source || "shop",
        quantity: wo.quantity || 1,
        artwork_notes: wo.artwork_notes || "",
        notes: wo.notes || "",
        deadline: wo.deadline || null,
        status: nextStatus,
        artwork_file_path: wo.artwork_file_path,
        artwork_file_name: wo.artwork_file_name,
      };
      const { data: updated } = await api.put(`/work-orders/${id}`, payload);
      const emailResult = updated?.auto_email_result;
      if (emailResult?.status === "sent") {
        toast.success(`Status → ${STATUS_LABELS[nextStatus] || nextStatus}. Invoice emailed to ${emailResult.to}.`);
      } else if (emailResult?.status === "skipped") {
        const reason = ({
          no_recipient_email: "no customer email on file",
          email_not_configured: "email is not configured yet",
        })[emailResult.reason] || emailResult.reason;
        toast.info(`Status → ${STATUS_LABELS[nextStatus] || nextStatus}. Invoice not auto-emailed: ${reason}.`);
      } else if (emailResult?.status === "failed") {
        toast.error(`Status changed but auto-email failed: ${emailResult.error}`);
      } else {
        toast.success(`Status → ${STATUS_LABELS[nextStatus] || nextStatus}`);
      }
      load();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const pdfUrl = `${API_BASE}/work-orders/${id}/pdf`;
  const isImage = (wo.artwork_file_name || "").toLowerCase().match(/\.(png|jpe?g|webp|gif)$/);

  return (
    <div>
      <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-900 text-sm mb-3 flex items-center gap-1" data-testid="wo-back-btn">
        <ArrowLeft size={16} /> Back
      </button>
      {wo.voided && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-md" style={{ background: "#FCECE9", border: "1px solid #F3B2A6", color: "#A33522" }} data-testid="wo-voided-banner">
          <Prohibit size={18} weight="fill" />
          <div className="text-sm">
            <b>Voided</b>{wo.voided_by ? ` by ${wo.voided_by}` : ""}{wo.voided_at ? ` on ${fmtDate(wo.voided_at)}` : ""}. Unlinked from any invoice. Cannot be reversed.
          </div>
        </div>
      )}
      {!wo.voided && wo.archived && (
        <div className="mb-4 flex items-center justify-between gap-3 px-4 py-3 rounded-md" style={{ background: "#FEF5E6", border: "1px solid #FCE0B3", color: "#B36B00" }} data-testid="wo-archived-banner">
          <div className="text-sm">This work order is <b>archived</b>. Restore to make it active again.</div>
          <button className="btn-ghost text-sm flex items-center gap-2" onClick={restore} data-testid="wo-restore-btn">
            <ArrowClockwise size={14} /> Restore
          </button>
        </div>
      )}
      {!wo.voided && !wo.archived && wo.locked && (
        <div className="mb-4 flex items-center justify-between gap-3 px-4 py-3 rounded-md" style={{ background: "#F0F0ED", border: "1px solid #DCDCD7", color: "#595956" }} data-testid="wo-locked-banner">
          <div className="text-sm flex items-center gap-2">
            <Lock size={14} weight="fill" className="text-slate-500" />
            Spec fields are <b>locked</b>. Edit the linked invoice to unlock, or click Unlock.
          </div>
          <button className="btn-ghost text-sm flex items-center gap-2" onClick={toggleLock} data-testid="wo-unlock-btn">
            <LockOpen size={14} /> Unlock
          </button>
        </div>
      )}
      <PageHeader
        title={wo.number}
        subtitle={`Work Order · ${customer?.name || ""}`}
        actions={
          <>
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn-ghost flex items-center gap-2" data-testid="wo-pdf-btn">
              <FilePdf size={16} /> Print PDF
            </a>
            {!wo.locked && !wo.archived && (
              <button className="btn-ghost flex items-center gap-2" onClick={toggleLock} data-testid="wo-lock-btn" title="Lock work order to prevent accidental spec edits">
                <Lock size={16} />
              </button>
            )}
            <button className="btn-ghost flex items-center gap-2" onClick={() => setEdit((e) => !e)} data-testid="wo-edit-toggle" disabled={wo.locked && !edit}>
              <PencilSimple size={16} /> {edit ? "Cancel" : "Edit"}
            </button>
            <button className="btn-primary flex items-center gap-2" onClick={convertToInvoice} data-testid="wo-convert-btn">
              <Receipt size={16} /> {invoice ? "Open Invoice" : "Convert to Invoice"}
            </button>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 surface p-6">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <span className={`chip ${wo.job_type}`}>{wo.job_type}</span>
            <span className={`chip ${normalizeStatus(wo.status)}`}>{STATUS_LABELS[normalizeStatus(wo.status)] || wo.status}</span>
            {wo.deadline && <span className="text-sm text-slate-500 font-mono">Due {fmtDate(wo.deadline)}</span>}
          </div>

          {/* Production workflow — stepper with quick-transition buttons */}
          <WorkflowStepper status={normalizeStatus(wo.status)} onChange={setStatus} hasEmail={!!customer?.email} />

          {!edit ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <KV label="Material" value={wo.material} />
              <KV label="Color" value={wo.color} />
              <KV label="Thickness" value={wo.thickness} />
              <KV label="Dimensions" value={wo.dimensions} />
              <KV label="Quantity" value={wo.quantity} />
              <KV label="Material From" value={(wo.material_source || "shop").toUpperCase()} />
              <KV label="Status" value={STATUS_LABELS[normalizeStatus(wo.status)] || wo.status} />
              <KV label="Job Type" value={wo.job_type.toUpperCase()} />
              <KV label="Created" value={fmtDate(wo.created_at)} />

              {wo.artwork_notes && (
                <div className="col-span-full mt-2">
                  <p className="text-slate-900 font-medium whitespace-pre-wrap">{wo.artwork_notes}</p>
                  <div className="overline mt-1">Artwork Notes</div>
                </div>
              )}
              {wo.notes && (
                <div className="col-span-full mt-2">
                  <p className="text-slate-900 font-medium whitespace-pre-wrap">{wo.notes}</p>
                  <div className="overline mt-1">Internal Notes</div>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Job Type</Label>
                <Select value={form.job_type} onValueChange={(v) => setForm({ ...form, job_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="laser">Laser Engraving</SelectItem>
                    <SelectItem value="uv">UV Flatbed Printing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Status</Label>
                <Select value={normalizeStatus(form.status)} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="wo-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="in_production">In Production</SelectItem>
                    <SelectItem value="fulfilled">Fulfilled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {["material","dimensions","color","thickness"].map((k) => (
                <div key={k}>
                  <Label className="text-xs text-slate-600 capitalize">{k}</Label>
                  <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`wo-edit-${k}`} />
                </div>
              ))}
              <div>
                <Label className="text-xs text-slate-600">Material From</Label>
                <Select value={form.material_source || "shop"} onValueChange={(v) => setForm({ ...form, material_source: v })}>
                  <SelectTrigger data-testid="wo-edit-material-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shop">Shop</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Quantity</Label>
                <Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Deadline</Label>
                <DateInput value={form.deadline || ""} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs text-slate-600">Artwork Notes</Label>
                <Textarea rows={2} value={form.artwork_notes || ""} onChange={(e) => setForm({ ...form, artwork_notes: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs text-slate-600">Internal Notes</Label>
                <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 mt-2">
                <button className="btn-ghost" onClick={() => { setEdit(false); setForm(wo); }}>Cancel</button>
                <button className="btn-primary" onClick={save} data-testid="wo-save-btn">Save</button>
              </div>
            </div>
          )}
        </div>

        {/* Items pulled live from the linked invoice — always in sync */}
        {invoice && invoice.items && invoice.items.length > 0 && (
          <div className="lg:col-span-2 surface p-6" data-testid="wo-items-section">
            <div className="overline mb-4">Items From Invoice {invoice.number}</div>
            <table className="data-table">
              <thead>
                <tr><th>#</th><th>Description</th><th className="text-right">Qty</th></tr>
              </thead>
              <tbody>
                {invoice.items.map((it, i) => (
                  <tr key={i} className="hover:bg-transparent cursor-default">
                    <td className="font-mono text-slate-500">{i + 1}</td>
                    <td className="text-slate-900 font-medium whitespace-pre-wrap">{it.description || "—"}</td>
                    <td className="text-right font-mono">{Number(it.quantity) || 1}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-slate-400 mt-3">Editing the invoice&apos;s line items automatically updates this list and the printed Work Order PDF.</p>
          </div>
        )}

        <ActivityLog woId={wo.id} entries={wo.activity_log || []} onChange={load} />

        <div className="flex flex-col gap-6">
          <div className="surface p-5">
            <div className="overline mb-3">Artwork</div>
            {wo.artwork_file_path ? (
              <>
                {isImage ? (
                  <img
                    src={`${API_BASE}/files/${wo.artwork_file_path}`}
                    alt="Artwork"
                    className="w-full border hairline"
                    data-testid="wo-artwork-img"
                  />
                ) : (
                  <a href={`${API_BASE}/files/${wo.artwork_file_path}`} target="_blank" rel="noreferrer" className="btn-ghost flex items-center gap-2 justify-center">
                    <DownloadSimple size={16} /> {wo.artwork_file_name}
                  </a>
                )}
              </>
            ) : (
              <div className="text-sm text-slate-400">No artwork attached.</div>
            )}
            <label className="mt-4 btn-ghost cursor-pointer text-sm inline-flex items-center gap-2">
              Upload {wo.artwork_file_path ? "replacement" : "artwork"}
              <input type="file" className="hidden" data-testid="wo-detail-upload" onChange={(e) => e.target.files?.[0] && uploadArtwork(e.target.files[0])} />
            </label>
            {edit && form.artwork_file_name !== wo.artwork_file_name && (
              <p className="mt-2 text-xs text-amber-400">New file staged — click Save.</p>
            )}
          </div>

          <div className="surface p-5">
            <div className="overline mb-3">Customer</div>
            <Link to={`/customers/${customer?.id}`} className="text-slate-900 font-medium hover:underline">{customer?.name}</Link>
            <div className="text-sm text-slate-500 mt-1">{customer?.company}</div>
            <div className="text-xs font-mono text-slate-400 mt-2">{customer?.email}</div>
            <div className="text-xs font-mono text-slate-400">{customer?.phone}</div>
          </div>

          {invoice ? (
            <div className="surface p-5">
              <div className="overline mb-2">Linked Invoice</div>
              <Link to={`/invoices/${invoice.id}`} className="text-slate-900 font-mono flex items-center gap-2 hover:underline" data-testid="wo-linked-invoice">
                {invoice.number} <ArrowRight size={14} />
              </Link>
            </div>
          ) : null}

          <div className="surface p-5" data-testid="wo-signature-card">
            <div className="flex items-center justify-between mb-3">
              <div className="overline flex items-center gap-2"><Signature size={14} /> Signature</div>
              {wo.signature_file_path && (
                <button onClick={clearSignature} className="text-xs text-red-400 hover:text-red-300" data-testid="wo-signature-clear">
                  Clear
                </button>
              )}
            </div>
            {wo.signature_file_path ? (
              <div>
                <img
                  src={`${API_BASE}/files/${wo.signature_file_path}`}
                  alt="Signature"
                  className="w-full border hairline"
                  style={{ background: "#fff" }}
                  data-testid="wo-signature-img"
                />
                <div className="mt-3 text-sm">
                  <div className="text-slate-900 font-medium">{wo.signed_by}</div>
                  <div className="font-mono text-xs text-slate-400">
                    {wo.signed_at ? new Date(wo.signed_at).toLocaleString() : ""}
                  </div>
                </div>
              </div>
            ) : (
              <SignaturePad onSave={saveSignature} saving={savingSig} />
            )}
          </div>

          {isOwner && !wo.voided && (
            <button className="btn-ghost text-red-500 hover:text-red-600 flex items-center justify-center gap-2" onClick={() => setVoidOpen(true)} data-testid="wo-void-btn">
              <Prohibit size={16} /> Void Work Order
            </button>
          )}
        </div>
      </div>
      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Prohibit size={22} weight="fill" /> Void Work Order</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="text-sm text-slate-700 leading-relaxed">
              Voiding <span className="font-mono font-semibold">{wo.number}</span> is <b>permanent</b>. This will:
            </div>
            <ul className="text-sm text-slate-700 space-y-1 pl-4 list-disc">
              <li>Mark it voided (removed from all active lists)</li>
              <li>Unlink any linked invoice (the invoice stays intact)</li>
              <li>Cannot be undone</li>
            </ul>
            <div>
              <Label className="text-xs text-slate-600">Enter your owner password to confirm</Label>
              <Input
                type="password"
                autoFocus
                value={voidPassword}
                onChange={(e) => setVoidPassword(e.target.value)}
                data-testid="wo-void-password"
                placeholder="Your password"
              />
            </div>
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => { setVoidOpen(false); setVoidPassword(""); }}>Cancel</button>
            <button
              className="btn-primary flex items-center gap-2"
              style={{ background: "#DC2626" }}
              onClick={doVoid}
              disabled={voiding || !voidPassword}
              data-testid="wo-void-confirm"
            >
              <Prohibit size={16} /> {voiding ? "Voiding…" : "Void Permanently"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const KV = ({ label, value }) => (
  <div>
    <div className="text-slate-900 font-medium">{value || "—"}</div>
    <div className="overline mt-1">{label}</div>
  </div>
);

function ActivityLog({ woId, entries, onChange }) {
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post(`/work-orders/${woId}/activity`, { text });
      setDraft("");
      onChange?.();
    } catch { toast.error("Could not add entry"); }
    finally { setBusy(false); }
  };
  const saveEdit = async (eid) => {
    const text = editText.trim();
    if (!text) return;
    try {
      await api.put(`/work-orders/${woId}/activity/${eid}`, { text });
      setEditingId(null); setEditText("");
      onChange?.();
    } catch { toast.error("Could not update entry"); }
  };
  const remove = async (eid) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/work-orders/${woId}/activity/${eid}`);
      onChange?.();
    } catch { toast.error("Could not delete entry"); }
  };
  const sorted = [...entries].sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  return (
    <div className="lg:col-span-2 surface p-6" data-testid="wo-activity-log">
      <div className="overline mb-4">Activity Log</div>
      <div className="flex gap-2 mb-4">
        <Textarea
          rows={2}
          placeholder="Add a note or activity entry…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="wo-activity-draft"
        />
        <button
          className="btn-primary self-stretch flex items-center gap-1 whitespace-nowrap"
          onClick={add}
          disabled={busy || !draft.trim()}
          data-testid="wo-activity-add"
        >
          <Plus size={14} /> Add
        </button>
      </div>
      {sorted.length === 0 ? (
        <div className="text-sm text-slate-400 py-4">No entries yet. Log calls, production updates, delivery notes, etc.</div>
      ) : (
        <ul className="flex flex-col gap-3">
          {sorted.map((e) => (
            <li key={e.id} className="border hairline rounded-md p-3" data-testid={`wo-activity-${e.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-xs text-slate-500 font-mono">
                  {fmtDate(e.at)} · <span className="text-slate-700">{e.by || "—"}</span>
                  {e.edited_at && <span className="text-slate-400"> · edited {fmtDate(e.edited_at)}</span>}
                </div>
                {editingId === e.id ? (
                  <div className="flex gap-1">
                    <button className="text-emerald-700 hover:text-emerald-900 p-1" onClick={() => saveEdit(e.id)} title="Save"><Check size={14} /></button>
                    <button className="text-slate-500 hover:text-slate-900 p-1" onClick={() => { setEditingId(null); setEditText(""); }} title="Cancel"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="flex gap-1">
                    <button className="text-slate-500 hover:text-slate-900 p-1" onClick={() => { setEditingId(e.id); setEditText(e.text); }} title="Edit"><PencilSimple size={14} /></button>
                    <button className="text-slate-400 hover:text-red-500 p-1" onClick={() => remove(e.id)} title="Delete"><Trash size={14} /></button>
                  </div>
                )}
              </div>
              {editingId === e.id ? (
                <Textarea rows={2} value={editText} onChange={(ev) => setEditText(ev.target.value)} />
              ) : (
                <div className="text-sm text-slate-900 whitespace-pre-wrap">{e.text}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const STEPS = [
  { key: "new", label: "New", Icon: Package, action: "Reopen" },
  { key: "in_production", label: "In Production", Icon: Factory, action: "Send to Production" },
  { key: "fulfilled", label: "Fulfilled", Icon: CheckCircle, action: "Mark Fulfilled" },
  { key: "completed", label: "Completed", Icon: Receipt, action: "Mark Completed" },
];

function WorkflowStepper({ status, onChange, hasEmail }) {
  const currentIdx = STEPS.findIndex((s) => s.key === status);
  return (
    <div className="mb-6 border hairline p-4" data-testid="wo-workflow">
      <div className="flex items-center justify-between mb-4">
        <div className="overline">Production Workflow</div>
        {status === "fulfilled" && (
          <span className="text-xs text-emerald-400 font-mono uppercase tracking-widest">✓ Ready to invoice</span>
        )}
      </div>
      <ol className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-1 mb-4">
        {STEPS.map((s, i) => {
          const done = i <= currentIdx;
          const active = i === currentIdx;
          return (
            <li key={s.key} className="flex items-center gap-2 sm:flex-1" data-testid={`wo-step-${s.key}`}>
              <div
                className={`w-8 h-8 flex items-center justify-center border ${
                  active
                    ? "border-emerald-700 bg-emerald-700 text-white"
                    : done
                    ? "border-emerald-500/60 text-emerald-700 bg-emerald-50"
                    : "border-slate-300 text-slate-400 bg-white"
                }`}
                title={s.label}
              >
                <s.Icon size={16} weight={done ? "fill" : "regular"} />
              </div>
              <span className={`text-xs uppercase tracking-widest ${active ? "text-slate-900" : done ? "text-slate-600" : "text-slate-400"}`}>
                {s.label}
              </span>
              {i < STEPS.length - 1 && <span className="hidden sm:block flex-1 h-px bg-slate-100 mx-2" />}
            </li>
          );
        })}
      </ol>
      <div className="flex flex-wrap gap-2">
        {status !== "in_production" && status !== "fulfilled" && status !== "completed" && (
          <button className="btn-primary text-sm flex items-center gap-2" onClick={() => onChange("in_production")} data-testid="wo-action-in-production">
            <Factory size={14} /> Send to Production
          </button>
        )}
        {(status === "in_production" || status === "new") && (
          <button className="btn-primary text-sm flex items-center gap-2" onClick={() => onChange("fulfilled")} data-testid="wo-action-fulfilled">
            <CheckCircle size={14} /> Mark Fulfilled
          </button>
        )}
        {status === "fulfilled" && (
          <button className="btn-ghost text-sm flex items-center gap-2" onClick={() => onChange("completed")} data-testid="wo-action-completed">
            <Receipt size={14} /> Mark Completed
          </button>
        )}
        {status !== "new" && (
          <button className="btn-ghost text-sm flex items-center gap-2 ml-auto text-slate-500 hover:text-slate-900" onClick={() => onChange("new")} data-testid="wo-action-reopen">
            <PaperPlaneTilt size={14} /> Reopen as New
          </button>
        )}
      </div>
    </div>
  );
}
